<template>
  <div>
    <div class="d-print-none">
      <p class="fs-lg font-weight-bold">
        <b-icon icon="info-circle" class="mr-1" />
        Je potrebné, aby ste správne zadali distribučnú oblasť, kde sa odberné
        miesto nachádza, vyberte typ RK a hodnotu RK. Kalkuláciu plánujte na 12
        mesiacov.
      </p>
      <p>
        Je dôležité, aby ste dodržali správnu kombináciu jednotlivých typov
        kapacít, tak, aby následne boli schválené prevádzkovateľom distribučnej
        siete.
      </p>
    </div>
    <b-row>
      <b-col md="6">
        <b-card class="mb-4" no-body>
          <b-card-body>
            <table
              class="table table-sm table-tp table-borderless table-middle fs-base"
            >
              <thead>
                <tr>
                  <th class="p-0" colspan="2">
                    <h5 class="card-title">Vstupné údaje</h5>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Distrib. oblasť</td>
                  <td>
                    <b-form-select
                      v-model.lazy="input.distributionArea"
                      v-validate="'required'"
                      class="d-print-none"
                      :class="{ 'is-invalid': errors.has('distributionArea') }"
                      :select-size="1"
                      data-vv-as="Distrib. oblasť"
                      name="distributionArea"
                      @change="calc"
                    >
                      <option :value="null">--Zvolte--</option>
                      <option
                        v-for="(item, key) in selection.distributionArea"
                        :key="key"
                        :value="key"
                      >
                        {{ item }}
                      </option>
                    </b-form-select>
                    <span class="d-none d-print-block">
                      {{ selection.distributionArea[input.distributionArea] }}
                    </span>
                    <b-form-invalid-feedback>
                      {{ errors.first('distributionArea') }}
                    </b-form-invalid-feedback>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <transition mode="out-in" name="fadeUpShort">
      <b-card v-if="input.distributionArea" class="mb-4" no-body>
        <b-card-body>
          <div class="table-responsive">
            <table class="table table-sm table-tp table-striped table-middle">
              <thead>
                <tr>
                  <th class="p-0 border-bottom-0" colspan="5">
                    <h5 class="card-title">
                      Položky {{ items.length > 0 ? `(${items.length})` : '' }}
                    </h5>
                  </th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th>Typ RK</th>
                  <th class="text-right">Hodnota (kW)</th>
                  <th class="text-right">Počet mesiacov</th>
                  <th class="text-right">Jedn. cena (€ bez dph)</th>
                  <th class="text-right">Suma (€ bez dph)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in items" :key="`item-${i}`">
                  <td>
                    <b-form-select
                      v-model="item.rkType"
                      v-validate="'required'"
                      :class="{ 'is-invalid': errors.has(`rkType${i}`) }"
                      class="d-print-none"
                      :name="`rkType${i}`"
                      :select-size="1"
                      data-vv-as="Typ RK"
                      size="sm"
                    >
                      <option :value="null">--Zvolte--</option>
                      <option
                        v-for="(item, key) in selection.rkType"
                        :key="key"
                        :value="key"
                      >
                        {{ item }}
                      </option>
                    </b-form-select>
                    <span class="d-none d-print-block">
                      {{ selection.rkType[item.rkType] }}
                    </span>
                    <b-form-invalid-feedback>
                      {{ errors.first(`rkType${i}`) }}
                    </b-form-invalid-feedback>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="item.value"
                      v-validate="'required|min_value:1'"
                      :class="{ 'is-invalid': errors.has(`value${i}`) }"
                      :name="`value${i}`"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 0,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                      data-vv-as="Hodnota (kW)"
                    />
                    <span class="text-right d-none d-print-block">{{
                      item.value | formatNumber
                    }}</span>
                    <b-form-invalid-feedback>
                      {{ errors.first(`value${i}`) }}
                    </b-form-invalid-feedback>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="item.months"
                      v-validate="'required|min_value:1|max_value:12'"
                      :class="{ 'is-invalid': errors.has(`months${i}`) }"
                      :name="`months${i}`"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 0,
                        maximumValue: 12,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                      data-vv-as="Počet mesiacov"
                      @input.native="checkMonths"
                    />
                    <span class="text-right d-none d-print-block">{{
                      item.months
                    }}</span>
                    <b-form-invalid-feedback>
                      {{ errors.first(`months${i}`) }}
                    </b-form-invalid-feedback>
                  </td>
                  <td class="text-right">
                    <template v-if="item.price">
                      {{ item.price | formatNumber(4) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                  <td class="text-right">
                    <template v-if="item.total">
                      {{ item.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr v-if="total" class="bg-transparent">
                  <td class="text-right" colspan="5">
                    <h5 class="fs-lg text-primary font-weight-bold">
                      <small class="fs-base">Celková suma (€ bez dph):</small>
                      {{ total | formatNumber(2) }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="validation.error">
              <span class="text-danger">{{ validation.text }}</span>
            </div>
            <b-btn
              v-if="months < 12"
              class="mt-2 d-print-none"
              size="sm"
              variant="outline-primary"
              @click.prevent="addRow"
            >
              <icon class="icon-fw" name="plus" />
            </b-btn>
          </div>
        </b-card-body>
      </b-card>
    </transition>
  </div>
</template>
<script>
import getPrices from '../mixins/getPrices'

export default {
  mixins: [getPrices],
  props: {
    year: {
      type: [String, Number],
    },
  },
  data() {
    return {
      loading: false,
      pricelist: [],
      input: {
        distributionArea: null,
      },
      items: [
        { rkType: null, price: null, value: null, months: null, total: null },
      ],
      total: null,
      validation: {
        error: false,
        text: null,
      },
    }
  },
  computed: {
    selection() {
      return {
        distributionArea: {
          vsd: 'VSD',
          ssd: 'SSD',
          zsdis: 'ZSDIS',
        },
        rkType: {
          1: 'Mesačná',
          3: '3-mesačná',
          12: '12-mesačná (ročná)',
        },
      }
    },
    months() {
      return this.items.reduce((sum, item) => item.months + sum, 0)
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.calc()
        this.total = this.items.reduce((sum, item) => item.total + sum, 0)
      },
    },
    year() {
      this.fetchPrices(true)
    },
  },
  created() {
    this.fetchPrices()
  },
  methods: {
    fetchPrices(calc = false) {
      this.getPrices(['monthly_tariff_rc'], calc)
    },
    addRow() {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any() && this.months < 12) {
          this.items.push({
            rkType: null,
            price: null,
            value: null,
            months: null,
            total: null,
          })
        }
      })
    },
    calc() {
      this.items.forEach((item) => {
        let price = this.pricelist.find(
          (price) =>
            price.name ===
            `monthly_tariff_rc_vn${item.rkType}_${this.input.distributionArea}`,
        )
        if (price) {
          item.price = price.values?.value
        }
        item.total = item.price * item.value * item.months
      })
    },
    checkMonths() {
      this.validation = {
        error: false,
        text: null,
      }
      if (this.months > 12) {
        this.validation.error = true
        this.validation.text = 'Celkový počet mesiacov musí byť 12 alebo menej'
      }
    },
    reset() {
      Object.assign(this.$data, this.$options.data())
      this.fetchPrices()
    },
  },
}
</script>
